import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { DEFAULT_MIDSTREAM_SETTINGS } from "constants/app.constants";
import { ORG_GEO_FIELDS, ORG_GEO_SIMS } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import { selectSelectedFacilities, selectSelectedWells, useMapStore } from "stores/map";

import { IGroupByListColumn, IGroupByListItem, Uwi } from "models";
import { EntityKind } from "models/entityKind";

import { useUserSettings } from "components/user/hooks";

import { useWellDetailsStore } from "../context/WellDetailsStoreProvider";
import {
  selectCurrentGroup,
  selectSetActiveGroup
} from "../context/wellDetailsSelectors";
import { GEO_GROUPS, isGeoGroup, useGeoJobReadiness } from "./useUnreadyGeoJobs";
import {
  deriveWellDetailsMessages,
  mergeWellDetailResults,
  useWellDetailGroupQuery
} from "./useWellDetailGroupQuery";

/**
 * Simple `hook` that abstracts API call to fetch well data
 */
interface UseWellDetailsDataProps {
  entityKind: EntityKind;
  /**
   * Pins the card to one well and ignores the map selection entirely. The well
   * card panel needs this; every widget mount leaves it undefined and keeps
   * following the selection.
   */
  uwi?: Maybe<string>;
}

const TYPE_LOG_GROUP = "Type Log";
/** Also the source of the always-visible band above the tabs. */
export const HEADER_GROUP = "Header";
const LICENSED_VOLUMES_GROUP = "Licensed Volumes";
const PERFORMANCE_GROUP = "Performance";
const TYPE_LOG_FIELD = "Geology.TypeLog";

/**
 * The two geo tabs, where one subgroup is one dropdown and each dropdown is
 * asked for on its own. A geo tab carries a subgroup for every calculation job
 * the org has ever run, so it is the tab that reaches thousands of fields and
 * minutes of service work — asking for the whole tab at once is what left it
 * empty or spinning.
 *
 * Every other tab is asked for whole and draws every subgroup open, the four
 * other custom-field tabs — My Fields, Organization Fields, Project Fields,
 * Shapefile & GeoTIFF — included. Each of those is a fixed, small set of
 * fields that one request answers, so splitting them bought no fetching worth
 * having and cost four tabs their appearance: a subgroup the reader used to
 * see became an expander they had to click.
 *
 * The same two tabs `GEO_GROUPS` names, and deliberately its own list: that
 * one is about what a geo subgroup means — a job name — and this one is about
 * a tab being too big to ask for at once.
 */
const SUBGROUP_REQUEST_GROUPS: string[] = [ORG_GEO_FIELDS, ORG_GEO_SIMS];

export function isSubgroupRequestGroup(group: string): boolean {
  return SUBGROUP_REQUEST_GROUPS.includes(group);
}

/**
 * Does this column sit under a subgroup of its own?
 *
 * The field carries whatever the service had, and it can come back absent or
 * empty. On a geo tab those columns are the tab's own rows: they are drawn
 * without a dropdown and asked for with the tab, which is how they behaved
 * before the tab was split up. Grouped instead, they end up under lodash's
 * `"undefined"` key and the card offers the user a dropdown headed
 * "undefined".
 *
 * Exported so the card partitions the rows by the same rule this hook asks the
 * service by. Split across the two, a column would be drawn in one place and
 * fetched in the other.
 */
export function hasOwnSubgroup(column: Pick<IGroupByListColumn, "subgroup">): boolean {
  return column.subgroup != null && column.subgroup !== "";
}

/** How far along one dropdown's own request is. */
export type SubgroupStatus = "loading" | "fetching" | "settled" | "failed";

/**
 * One request's identity: a tab, or one dropdown inside it. Stringified rather
 * than joined, because a tab or a job name is org-authored and may hold any
 * separator we could pick — two different pairs reading as the same key would
 * quietly hand one of them the other's frozen field list.
 */
function requestKey(group: string, subgroup: Maybe<string>): string {
  return JSON.stringify([group, subgroup ?? null]);
}

/** One entry in the request list: a whole tab, or one dropdown in it. */
interface RequestedUnit {
  group: string;
  subgroup: Maybe<string>;
}

const useWellDetailsData = ({ entityKind, uwi }: UseWellDetailsDataProps) => {
  // store values
  const wellCategories = useSelector((state: RootState) => state.groupBy.categoryList);
  const facilityCategories = useSelector(
    (state: RootState) => state.groupBy.facilityFields
  );
  const selectedWells = useMapStore(selectSelectedWells);
  const selectedFacilities = useMapStore(selectSelectedFacilities);
  const { defaultSignificantFigures, useStrictRounding } = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );
  const isWell = entityKind === EntityKind.Well;
  const isFacility = entityKind === EntityKind.Facility;

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const userSettings = useUserSettings();
  const isMidstreamDataSourceEnabled =
    userSettings?.midstreamSettings?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;

  const categories =
    entityKind === EntityKind.Facility ? facilityCategories : wellCategories;

  // The job lists are the whole org's, and the ticket opens for every user, so
  // they are asked for only when this ticket has a geo tab with columns in it.
  const hasGeoColumns = useMemo(
    () =>
      (categories ?? []).some(
        (category) => isGeoGroup(category.name) && category.columns?.length > 0
      ),
    [categories]
  );
  const geoJobs = useGeoJobReadiness(hasGeoColumns);

  // client selection — scoped WellDetailsStore, replaces `useWellDetails()`'s
  // `currentGroup`/`setActiveGroup`.
  const currentGroup = useWellDetailsStore(selectCurrentGroup);
  const setActiveGroup = useWellDetailsStore(selectSetActiveGroup);

  // states
  const [selectedWell, setSelectedWell] = useState<string>(null);
  const [selectedFacility, setSelectedFacility] = useState<string>(null);

  // React Query trigger list — replaces the old `loadedGroups` field's
  // *trigger* role (its *result-tracking* role is replaced by `isSuccess`
  // per request, derived below from `useWellDetailGroupQuery`'s results).
  // One entry is one request: a whole tab, or one dropdown inside a tab that
  // is split into them.
  // `fieldsByRequestRef` freezes each one's `fields` at the moment it's first
  // requested, mirroring the old `.mutate({ fields, ... })` call's frozen
  // request shape — recomputing `fields` for an already-requested tab on
  // every render would otherwise change its query key and trigger spurious
  // refetches.
  const [requestedUnits, setRequestedUnits] = useState<RequestedUnit[]>([]);
  const fieldsByRequestRef = useRef<Record<string, string[]>>({});
  // The geo readiness the fields above were built from, frozen with them. The
  // card reads this rather than the live job lists, so what it says about a job
  // and what it asked the service for cannot disagree.
  const unreadyGeoJobsRef = useRef<Record<string, Set<string>>>({});
  // Frozen on the same beat, and for the same reason: the tab's dropdown list
  // must not gain or lose a job while the user is reading it.
  const knownGeoJobsRef = useRef<Partial<Record<string, Set<string>>>>({});
  // The card has been pointed at a target and nothing has been asked for it
  // yet. Only then is an open Type Log tab a leftover from the previous target.
  const isNewTargetRef = useRef(true);

  // memos
  const skippedGroups = useMemo(() => ["Performance", "Forecast"], []);
  const headerProperties = useMemo(
    () => [
      "Header.EntityName",
      "Dates.Vintage_YearMonth",
      "Header.ResourcePlay",
      "Header.ResourceType",
      "Header.WellType",
      "Header.Status",
      "Header.Operator_Short"
    ],
    []
  );

  const facilityHeaderProperties = useMemo(
    () => [
      "Header.FacilityId",
      "Header.FacilityName",
      "Header.FacilityType",
      "Header.OperatorName",
      "Header.OperationalStatus",
      "Header.FacilityStartDate_YearMonth"
    ],
    []
  );

  const buildFieldsForGroup = useCallback(
    (group: string, subgroup: Maybe<string>) => {
      // A geo tab asks only for its own rows — the columns with no dropdown to
      // sit under. Everything else belongs to a dropdown and is asked for when
      // the user opens it, which is what puts the tab's dropdown list on screen
      // straight away with every one of them still unasked. A geo tab usually
      // has no such rows and the list comes back empty, which is reported as a
      // settled success and costs no request. Every other tab falls through to
      // the whole-tab list below, and the facility ticket has no geo tabs at
      // all, so both are untouched.
      //
      // Compared against null, never by truthiness, here and everywhere else
      // this pair is taken apart. Null is the tab; anything else is a dropdown,
      // and the two must not be told apart by a test that a dropdown name could
      // also fail. Nothing in the card sends an empty name today — `hasOwnSubgroup`
      // routes a column with no subgroup into the tab's own rows above — but
      // this is the one place that decides which of the two a request is, and
      // it should not rest on a caller it does not control.
      if (subgroup == null && isWell && isSubgroupRequestGroup(group)) {
        return [
          ...new Set(
            (categories ?? [])
              .filter((categoryGroup) => categoryGroup.name === group)
              .flatMap((categoryGroup) =>
                categoryGroup.columns
                  .filter((c) => !hasOwnSubgroup(c))
                  .map((c) => c.property)
              )
          )
        ];
      }

      // A geo column whose job has never run has no value to fetch. Left in,
      // the service walks every one of them and answers "n/a" — minutes of
      // work for rows the card can fill in without asking.
      //
      // Read from the tab's frozen copy wherever there is one, so a dropdown
      // leaves out exactly the jobs the tab drew a "No data available." message
      // for. The live lists can refetch between the tab being frozen and a
      // dropdown being opened, and a job that ENTERS the unready set in that
      // window would otherwise give the dropdown an empty field list — which
      // reports itself as a settled success and renders rows of "n/a" with no
      // message and nothing to retry. The live copy is the fallback for the
      // tab's own first request, which is the moment the freeze is taken.
      const unreadyJobs =
        unreadyGeoJobsRef.current[group] ?? geoJobs.unreadyJobNames[group];
      // Matched as a string, because the name the card asks with is one of the
      // keys lodash `groupBy` produced, and those are always strings. A column
      // carrying no subgroup at all cannot disagree with it: on a geo tab
      // `hasOwnSubgroup` keeps it out of `groupBy` entirely and the branch
      // above asks for it with the tab, and on every other tab the whole list
      // goes out under one request with no name to match against.
      const columnsToRequest = (categoryGroup: IGroupByListItem) =>
        categoryGroup.columns.filter(
          (c) =>
            (subgroup == null || String(c.subgroup) === subgroup) &&
            !unreadyJobs?.has(c.subgroup)
        );

      const fields = (categories ?? []).reduce((list, categoryGroup) => {
        if (group === categoryGroup.name) {
          list.push(...columnsToRequest(categoryGroup).map((c) => c.property));
        }
        if (
          group === LICENSED_VOLUMES_GROUP &&
          categoryGroup.name === PERFORMANCE_GROUP
        ) {
          list.push(...categoryGroup.columns.map((c) => c.property));
        }
        return list;
      }, []);
      // check only TypeLog property for PDF
      if (group === TYPE_LOG_GROUP) {
        fields.push(TYPE_LOG_FIELD);
      } else if (group === HEADER_GROUP) {
        // Header properties belong to the Header tab's own request, gated on
        // group identity rather than request order (code review: the old
        // "first request" heuristic broke when Type Log was requested before
        // Header via the Type-Log -> Header auto-redirect below — neither
        // request ended up carrying header fields and the header card
        // rendered blank).
        if (isFacility) {
          fields.push(...facilityHeaderProperties);
        } else {
          fields.push(...headerProperties);
        }
      }
      return [...new Set(fields)];
    },
    [categories, isWell, isFacility, facilityHeaderProperties, headerProperties, geoJobs]
  );

  /**
   * A request's fields are computed once, the first time it is asked for, and
   * held from then on. Re-freezing them mid-session changes the query key,
   * which discards a geo dropdown's progress and starts its minutes of chunks
   * again.
   *
   * The geo job readiness is frozen once per tab, by whichever of its requests
   * comes first, and everything downstream reads that one copy: the card's
   * "No data available." message, and every later dropdown's field list. So a
   * job the tab wrote that message for is a job no dropdown asks for, and a
   * job a dropdown does ask for is one the tab did not write the message for —
   * however many times the job lists refetch in between. Drop the freeze on
   * either side and the two disagree: a job that finishes mid-session loses its
   * message and shows "n/a" for columns nothing requested, and a job that turns
   * unready mid-session gets a dropdown with no fields and no message. The job
   * names the card is allowed to list are frozen on the same beat, so a
   * dropdown cannot vanish from under the user. Pointing the card at another
   * entity rebuilds all of it.
   *
   * A geo tab is not frozen at all until the job lists have answered: a list
   * still in flight says every job is ready, which is how a whole tab of
   * unfinished jobs got asked for and answered "n/a" row by row. The tab is
   * requested by the effect below once they land.
   */
  const requestUnit = useCallback(
    (group: string, subgroup: Maybe<string>) => {
      if (isGeoGroup(group) && !geoJobs.isSettled) return;
      isNewTargetRef.current = false;
      setRequestedUnits((prev) => {
        const key = requestKey(group, subgroup);
        if (prev.some((unit) => requestKey(unit.group, unit.subgroup) === key)) {
          return prev;
        }
        fieldsByRequestRef.current[key] = buildFieldsForGroup(group, subgroup);
        // Written once per group, by whichever of its requests comes first —
        // a dropdown opened later must read the same answer the tab was drawn
        // from, not whatever the job lists say by then.
        const unreadyJobs = geoJobs.unreadyJobNames[group];
        if (unreadyJobs && !(group in unreadyGeoJobsRef.current)) {
          unreadyGeoJobsRef.current[group] = unreadyJobs;
        }
        // Recorded even when absent (a non-geo group): the key is what blocks a
        // later refetch from filtering headings the tab already drew.
        if (!(group in knownGeoJobsRef.current)) {
          knownGeoJobsRef.current[group] = geoJobs.knownJobNames[group];
        }
        return [...prev, { group, subgroup }];
      });
    },
    [buildFieldsForGroup, geoJobs]
  );

  const requestGroup = useCallback(
    (group: string) => requestUnit(group, null),
    [requestUnit]
  );

  /** Called by the card the first time the user opens a tab's dropdown. */
  const requestSubgroup = useCallback(
    (group: string, subgroup: string) => requestUnit(group, subgroup),
    [requestUnit]
  );

  const resetRequestedGroups = useCallback(() => {
    fieldsByRequestRef.current = {};
    unreadyGeoJobsRef.current = {};
    knownGeoJobsRef.current = {};
    isNewTargetRef.current = true;
    setRequestedUnits([]);
  }, []);

  useEffect(() => {
    // A pinned uwi wins over the map selection, and over the selection being
    // cleared.
    if (uwi && entityKind === EntityKind.Well) {
      if (uwi !== selectedWell) {
        setSelectedWell(uwi);
        resetRequestedGroups();
      }
      return;
    }
    if (
      selectedWells &&
      Object.keys(selectedWells).length > 0 &&
      entityKind === EntityKind.Well &&
      activeEntities.includes(EntityKind.Well)
    ) {
      const newSelectedWell = selectedWells[Object.keys(selectedWells)[0]].Uwi;
      if (newSelectedWell !== selectedWell) {
        setSelectedWell(newSelectedWell);
        resetRequestedGroups();
      }
    }
  }, [
    uwi,
    selectedWells,
    entityKind,
    activeEntities,
    selectedWell,
    resetRequestedGroups
  ]);

  useEffect(() => {
    if (
      selectedFacilities &&
      Object.keys(selectedFacilities).length > 0 &&
      entityKind === EntityKind.Facility
    ) {
      const newSelectedFacility =
        selectedFacilities[Object.keys(selectedFacilities)[0]].Uwi;
      if (newSelectedFacility !== selectedFacility) {
        setSelectedFacility(newSelectedFacility);
        resetRequestedGroups();
      }
    }
    if (
      (entityKind === EntityKind.Facility &&
        !activeEntities.includes(EntityKind.Facility)) ||
      !isMidstreamDataSourceEnabled
    ) {
      setSelectedFacility(null);
    }

    // A pinned uwi is not a map selection, so hiding wells on the map must not
    // clear it — the effect above would only put it straight back.
    if (
      !uwi &&
      entityKind === EntityKind.Well &&
      !activeEntities.includes(EntityKind.Well)
    ) {
      setSelectedWell(null); // Remove from widget. Note: can still see in background.
      resetRequestedGroups(); // Clear well details on widget.
    }
  }, [
    uwi,
    selectedFacilities,
    entityKind,
    activeEntities,
    isMidstreamDataSourceEnabled,
    selectedFacility,
    resetRequestedGroups
  ]);

  // Reset requested groups when significant figures settings change to force
  // a refetch (a new `settingsKey` also changes the React Query cache key,
  // but the trigger list must be cleared too so the current group actually
  // gets re-requested).
  useEffect(() => {
    resetRequestedGroups();
  }, [defaultSignificantFigures, useStrictRounding, resetRequestedGroups]);

  const uwid = useMemo(() => {
    const selected = isWell ? selectedWell : selectedFacility;
    return selected ? new Uwi().toUnformatted(selected) : null;
  }, [isWell, selectedWell, selectedFacility]);

  const requests = useMemo(
    () =>
      requestedUnits.map(({ group, subgroup }) => ({
        group,
        subgroup,
        fields: fieldsByRequestRef.current[requestKey(group, subgroup)] ?? []
      })),
    [requestedUnits]
  );

  // Frozen with the fields above, and rebuilt on the same beat, so the card
  // never says a job has data the request left out.
  const unreadyGeoJobs = useMemo(
    () => ({ ...unreadyGeoJobsRef.current }),
    [requestedUnits]
  );

  // The job names the card may list as dropdowns, frozen alongside them.
  const knownGeoJobs = useMemo(() => ({ ...knownGeoJobsRef.current }), [requestedUnits]);

  const groupResults = useWellDetailGroupQuery({
    uwid,
    entityKind,
    requests,
    defaultSignificantFigures,
    useStrictRounding
  });

  // Read-latest-without-retriggering ref: the trigger effect below only
  // needs groupResults to check the CURRENT group's error state at the
  // moment currentGroup/requestedGroups change — it must not itself be a
  // dependency (a new array every render would fire the effect every render).
  const groupResultsRef = useRef(groupResults);
  groupResultsRef.current = groupResults;

  useEffect(() => {
    if (!selectedWell && isWell) return;
    if (!selectedFacility && isFacility) return;

    if (
      requestedUnits.some((unit) => unit.group === currentGroup && unit.subgroup == null)
    ) {
      // Code review: with retry:false, an errored group is never
      // auto-retried just because its query is still subscribed — the old
      // success-tracked loadedGroups guard let re-selecting an errored tab
      // retry it (a fetch that hadn't succeeded was, by definition, not in
      // loadedGroups, so revisiting it re-triggered the mutation). Restore
      // that by explicitly refetching when the current group's result is
      // in an error state. The tab's own request, not a dropdown's: a
      // dropdown reports its own trouble where the user opened it.
      const currentResult = groupResultsRef.current.find(
        (result) => result.group === currentGroup && result.subgroup == null
      );
      if (currentResult?.isError) {
        currentResult.refetch();
      }
      return;
    }
    // default to header tab when switching from well to well while in type log tab
    //
    // Only for a target nothing has been asked for yet. Reaching here already
    // proves the group has not been requested, so a check on the requested
    // list would be true for a first click on the tab as well and bounce the
    // user off it.
    if (currentGroup === TYPE_LOG_GROUP && isNewTargetRef.current) {
      setActiveGroup(HEADER_GROUP);
    }
    requestGroup(currentGroup);
  }, [
    selectedWell,
    selectedFacility,
    currentGroup,
    isWell,
    isFacility,
    requestedUnits,
    requestGroup,
    setActiveGroup
  ]);

  // The band above the tabs — name, vintage, well type, resource play, status,
  // operator — is always on screen, and its fields belong to the Header group.
  // Requesting only the open tab's group left that band with nothing to read
  // whenever a new target was picked while any other tab was active, so it
  // rendered "n/a" across the board until the user happened to click Header.
  // Requested for the facility ticket too: its own band is built from the same
  // group.
  useEffect(() => {
    if (!selectedWell && isWell) return;
    if (!selectedFacility && isFacility) return;
    requestGroup(HEADER_GROUP);
  }, [selectedWell, selectedFacility, isWell, isFacility, requestGroup]);

  const wellDetails = useMemo(
    () => mergeWellDetailResults(groupResults, entityKind),
    [groupResults, entityKind]
  );

  // Everything the tabs themselves are told comes from the tabs' own requests.
  // A dropdown's result says nothing about its tab: a geo tab is ready the
  // moment its dropdown list can be drawn, whatever any one dropdown is doing,
  // and one dropdown the service cannot answer must not paint the whole tab as
  // failed.
  const tabResults = useMemo(
    () => groupResults.filter((result) => result.subgroup == null),
    [groupResults]
  );

  const loadedGroups = useMemo(
    () => tabResults.filter((result) => result.isSuccess).map((result) => result.group),
    [tabResults]
  );

  // Groups whose chunks are still arriving. Such a group is already loaded —
  // its first chunk is on screen — so a field missing from it is "not here
  // yet", not "the service has no value for it".
  const fetchingGroups = useMemo(
    () => tabResults.filter((result) => result.isFetching).map((result) => result.group),
    [tabResults]
  );

  // A group whose fetch failed is neither loaded nor still loading. Without
  // this the card has no way to tell the two apart and shows the loading
  // spinner for ever.
  const failedGroups = useMemo(() => {
    const failed = tabResults
      .filter((result) => result.isError)
      .map((result) => ({ group: result.group, retry: result.refetch }));
    // A geo tab is never requested while a job list is down, so it has no result
    // of its own to fail. Without this it would sit on its spinner for ever.
    if (!geoJobs.isError) return failed;
    const blocked = GEO_GROUPS.filter(
      (group) =>
        !failed.some((entry) => entry.group === group) &&
        (categories ?? []).some(
          (category) => category.name === group && category.columns?.length > 0
        )
    ).map((group) => ({ group, retry: geoJobs.retry }));
    return [...failed, ...blocked];
  }, [tabResults, geoJobs.isError, geoJobs.retry, categories]);

  // One dropdown at a time. "loading" is nothing back yet, which is what the
  // card shows a spinner for; "fetching" is chunks still arriving behind the
  // rows already on screen; "failed" is the request having come back with
  // nothing to show, which the card says out loud with a Retry beside it —
  // left as rows of "n/a" it would be indistinguishable from a field the
  // service simply has no value for. Only a dropdown nobody has opened is
  // absent from here.
  //
  // A retry in flight reads as "fetching" rather than "failed", so the card
  // shows the click did something. `retry: false` on the query means an errored
  // one stays errored until somebody asks again.
  const subgroupStatus = useMemo(
    () =>
      groupResults.reduce((byGroup, result) => {
        if (result.subgroup == null) return byGroup;
        const status: SubgroupStatus = result.isLoading
          ? "loading"
          : result.isFetching
          ? "fetching"
          : result.isError
          ? "failed"
          : "settled";
        byGroup[result.group] = { ...byGroup[result.group], [result.subgroup]: status };
        return byGroup;
      }, {} as Record<string, Record<string, SubgroupStatus>>),
    [groupResults]
  );

  /**
   * Runs one dropdown's request again, for the card's Retry. Reads the results
   * through the ref rather than closing over them, so the card's tab tree is
   * not rebuilt every time a chunk lands just because this callback changed.
   */
  const retrySubgroup = useCallback((group: string, subgroup: string) => {
    groupResultsRef.current
      .find((result) => result.group === group && result.subgroup === subgroup)
      ?.refetch();
  }, []);

  // The whole ticket, not one dropdown inside it: the card puts a spinner over
  // everything for this, and one of thirty dropdowns still in flight is not the
  // ticket loading.
  const isLoading = tabResults.some((result) => result.isLoading);

  const currentGroupResult = useMemo(
    () => tabResults.find((result) => result.group === currentGroup),
    [tabResults, currentGroup]
  );
  const { error, typeLogMessage } = deriveWellDetailsMessages(currentGroupResult, uwid);

  return {
    selectedWell,
    selectedFacility,
    headerProperties,
    facilityHeaderProperties,
    skippedGroups,
    isLoading,
    error,
    typeLogMessage,
    wellDetails,
    loadedGroups,
    fetchingGroups,
    failedGroups,
    subgroupStatus,
    requestSubgroup,
    retrySubgroup,
    // The one thing that makes `requestSubgroup` decline. The card passes it to
    // each dropdown so a dropdown opened too early can ask again once the lists
    // land, rather than relying on this hook's callbacks changing identity.
    geoJobsSettled: geoJobs.isSettled,
    unreadyGeoJobs,
    knownGeoJobs
  };
};

export default useWellDetailsData;
